export const EMISSION_REMOVAL_MITIGATION_LABEL =
  "Emission Removal & Mitigation";

export const EMISSION_REMOVAL_MITIGATION_ENABLED = true;

export const getEnabledEmissionCategoryOptions = (options = []) =>
  EMISSION_REMOVAL_MITIGATION_ENABLED
    ? options
    : options.filter(
        (option) =>
          option?.value !== EMISSION_REMOVAL_MITIGATION_LABEL &&
          option?.label !== EMISSION_REMOVAL_MITIGATION_LABEL,
      );

export const RM_WORKFLOW_VIEWS = [
  "Emission Activity",
  "Emission Reduction",
  "Summary",
];

export const isEmissionRemovalMitigation = (scope) =>
  scope === EMISSION_REMOVAL_MITIGATION_LABEL;

export const RM_ACTIVITY_TYPES = [
  { value: "62001", label: "Fuel Saving / Efficiency" },
  { value: "62002", label: "Energy Saving / Efficiency" },
  { value: "62003", label: "Renewable Energy" },
  { value: "62004", label: "Transport Route Optimization" },
  { value: "62005", label: "Afforestation" },
  { value: "62006", label: "Waste Amount Reduction" },
  { value: "62007", label: "Alternative Raw Materials" },
  { value: "62008", label: "Low GWP Refrigerant" },
  {
    value: "62009",
    label: "Switch to Low Carbon Footprint Energy / Fuel",
  },
  { value: "62010", label: "Others" },
];

export const RM_ACTIVITY_TYPE_OTHERS = "62010";

export const formatRMActivityType = (value) => {
  const type = RM_ACTIVITY_TYPES.find(
    (item) => item.value === value || item.label === value,
  );
  return type?.label || value || "-";
};

export const RM_SCOPE_OPTIONS = ["1", "2", "3"];

export const getRMScenario = ({ q1, q2 }) => {
  if (q1 === "yes") return "S1";
  if (q2 === "yes") return "S2";
  return "S3";
};

export const getRMScenarioMeta = (scenario) => {
  const map = {
    S1: {
      label: "S1 - Cross category + new source",
      shortLabel: "S1",
      description: "Cross category + New source",
      className: "scenarioS1",
    },
    S2: {
      label: "S2 - Same category + new source",
      shortLabel: "S2",
      description: "Same category + New source",
      className: "scenarioS2",
    },
    S3: {
      label: "S3 - Same category + same source",
      shortLabel: "S3",
      description: "Same category + Same source",
      className: "scenarioS3",
    },
  };

  return map[scenario] || map.S3;
};

export const formatRMScopeCategory = (scope, category) =>
  scope && category ? `Scope ${scope} / ${category}` : "-";

export const formatRMNumber = (value, digits = 4) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits) : "-";
};

export const createRMActivity = (activity) => {
  return {
    ...activity,
    before: activity.before || null,
    after: activity.after || null,
  };
};

export const calculateRMActivity = (activity) => {
  const before = activity?.before;
  const after = activity?.after;

  if (!before || !after) {
    return { reducedData: null, reduced: 0, increased: 0, net: 0 };
  }

  if (activity.scenario === "S3") {
    const reducedData = before.data - after.data;
    const reduced = before.ef * reducedData;
    return {
      reducedData,
      reduced,
      increased: 0,
      net: reduced,
    };
  }

  const replacementRate = (Number(activity.replacementPct) || 0) / 100;
  const baseReduced = before.ef * before.data * replacementRate;

  if (activity.scenario === "S1") {
    const increased = after.ef * after.data;
    return {
      reducedData: null,
      reduced: baseReduced,
      increased,
      net: baseReduced - increased,
    };
  }

  const reduced = baseReduced - after.ef * after.data;
  return {
    reducedData: null,
    reduced,
    increased: 0,
    net: reduced,
  };
};

export const getRMFormula = (scenario, full = true) => {
  if (scenario === "S1") {
    return full
      ? "Reduced = EF_base x Base Data x Replacement%; Increased = EF_report x Report Data"
      : "EF_b x Base x Repl%; EF_r x Report";
  }

  if (scenario === "S2") {
    return full
      ? "Reduced = (EF_base x Base Data x Replacement%) - (EF_report x Report Data)"
      : "(EF_b x Base x Repl%) - (EF_r x Report)";
  }

  return full
    ? "Reduced Activity Data = Base Data - Report Data; Reduced = EF x data difference"
    : "EF x (Base - Report)";
};

const hasValue = (value) => value !== null && value !== undefined;

const getScopeFromCategory = (category) => {
  if (!category) return "";
  const value = String(category);
  return value.includes(".") ? value.split(".")[0] : value.split("_")[0];
};

const normalizeRMSummaryActivity = (activity) => {
  const calculated = calculateRMActivity(activity);
  const reduced = hasValue(activity.reducedEmission)
    ? Number(activity.reducedEmission) || 0
    : calculated.reduced;
  const increased = hasValue(activity.increasedEmission)
    ? Number(activity.increasedEmission) || 0
    : calculated.increased;
  const beforeCategory = activity.beforeCategory || "";
  const afterCategory = activity.afterCategory || "";

  return {
    ...activity,
    entity: activity.companyName || activity.entity || "-",
    name: activity.itemName || activity.name || "-",
    rmType: activity.activityName || activity.rmType || "-",
    beforeCategory,
    afterCategory,
    beforeScope: activity.beforeScope || getScopeFromCategory(beforeCategory),
    afterScope: activity.afterScope || getScopeFromCategory(afterCategory),
    result: {
      ...calculated,
      reduced,
      increased,
      net: reduced - increased,
    },
  };
};

export const getRMSummary = (activities = []) => {
  const rows = activities.map(normalizeRMSummaryActivity);

  const totalReduced = rows.reduce((sum, item) => sum + item.result.reduced, 0);
  const totalIncreased = rows.reduce(
    (sum, item) => sum + item.result.increased,
    0,
  );
  const net = totalReduced - totalIncreased;

  const summarizeGroup = (key, group, extra = {}) => {
    const reduced = group.reduce((sum, item) => sum + item.result.reduced, 0);
    const increased = group.reduce(
      (sum, item) => sum + item.result.increased,
      0,
    );

    return {
      key,
      activities: group.length,
      reduced,
      increased,
      net: reduced - increased,
      ...extra,
    };
  };

  const groupBy = (keys, getter, getExtra = () => ({})) =>
    keys
      .map((key) => {
        const group = rows.filter((item) => getter(item) === key);
        if (!group.length) return null;

        return summarizeGroup(key, group, getExtra(key));
      })
      .filter(Boolean);

  const categoryBuckets = {};
  rows.forEach((item) => {
    const reducedKey = `${item.beforeScope}|${item.beforeCategory}`;
    categoryBuckets[reducedKey] = categoryBuckets[reducedKey] || {
      scope: item.beforeScope,
      category: item.beforeCategory,
      reduced: 0,
      increased: 0,
      activities: new Set(),
    };
    categoryBuckets[reducedKey].reduced += item.result.reduced;
    categoryBuckets[reducedKey].activities.add(item.id);

    if (item.scenario === "S1") {
      const increasedKey = `${item.afterScope}|${item.afterCategory}`;
      categoryBuckets[increasedKey] = categoryBuckets[increasedKey] || {
        scope: item.afterScope,
        category: item.afterCategory,
        reduced: 0,
        increased: 0,
        activities: new Set(),
      };
      categoryBuckets[increasedKey].increased += item.result.increased;
      categoryBuckets[increasedKey].activities.add(item.id);
    }
  });

  const scopeBreakdownForEntity = (entity) => {
    const entityRows = rows.filter((item) => item.entity === entity);

    return RM_SCOPE_OPTIONS.reduce((acc, scope) => {
      const reduced = entityRows
        .filter((item) => item.beforeScope === scope)
        .reduce((sum, item) => sum + item.result.reduced, 0);
      const increased = entityRows
        .filter((item) => item.scenario === "S1" && item.afterScope === scope)
        .reduce((sum, item) => sum + item.result.increased, 0);

      if (reduced || increased) {
        acc[scope] = {
          scope,
          label: `Scope ${scope}`,
          reduced,
          increased,
          net: reduced - increased,
        };
      }

      return acc;
    }, {});
  };

  const entityKeys = [...new Set(rows.map((item) => item.entity))];
  const byEntity = groupBy(entityKeys, (item) => item.entity, (entity) => ({
    scopeBreakdown: scopeBreakdownForEntity(entity),
  }));

  const scopeDetail = byEntity.map((entity) => ({
    entity: entity.key,
    scopes: Object.values(entity.scopeBreakdown),
    total: {
      reduced: entity.reduced,
      increased: entity.increased,
      net: entity.net,
    },
  }));

  const byScope = [];
  const sortedScopes = [
    ...new Set(Object.values(categoryBuckets).map((item) => item.scope)),
  ].sort();

  sortedScopes.forEach((scope) => {
    const categories = Object.values(categoryBuckets)
      .filter((item) => item.scope === scope)
      .sort((a, b) => a.category.localeCompare(b.category));
    const scopeTotal = {
      reduced: 0,
      increased: 0,
      activities: new Set(),
    };

    categories.forEach((item) => {
      item.activities.forEach((id) => scopeTotal.activities.add(id));
      scopeTotal.reduced += item.reduced;
      scopeTotal.increased += item.increased;

      if (item.reduced) {
        byScope.push({
          key: `scope-${scope}-${item.category}-reduced`,
          label: formatRMScopeCategory(scope, item.category),
          direction: "Reduced",
          activities: item.activities.size,
          emission: item.reduced,
          netChange: -item.reduced,
          isTotal: false,
        });
      }

      if (item.increased) {
        byScope.push({
          key: `scope-${scope}-${item.category}-increased`,
          label: formatRMScopeCategory(scope, item.category),
          direction: "Increased",
          activities: item.activities.size,
          emission: item.increased,
          netChange: item.increased,
          isTotal: false,
        });
      }
    });

    byScope.push({
      key: `scope-${scope}-total`,
      label: `Scope ${scope} - Total`,
      direction: "Total",
      activities: scopeTotal.activities.size,
      reduced: scopeTotal.reduced,
      increased: scopeTotal.increased,
      net: scopeTotal.reduced - scopeTotal.increased,
      netChange: scopeTotal.increased - scopeTotal.reduced,
      isTotal: true,
    });
  });

  return {
    rows,
    kpis: [
      { label: "Total Activities", value: activities.length },
      { label: "Total Reduced Emission", value: formatRMNumber(totalReduced) },
      {
        label: "Total Increased Emission",
        value: formatRMNumber(totalIncreased),
      },
      { label: "Net Emission Reduction", value: formatRMNumber(net) },
    ],
    totals: {
      totalReduced,
      totalIncreased,
      net,
    },
    byScenario: groupBy(["S1", "S2", "S3"], (item) => item.scenario, (key) => ({
      description: getRMScenarioMeta(key).description,
    })),
    byEntity,
    scopeDetail,
    byScope,
  };
};
